import { z } from 'zod';
/**
 * **Contrats d'entrée de `CDC-1`, volet provisionnement et relations.**
 *
 * Mêmes conventions que `acces.contract.ts` : `strictObject`, `.describe()` sur
 * chaque champ (D-024).
 */
const email = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254)
    .refine((valeur) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valeur), {
    message: 'doit être une adresse email',
});
const nom = z.string().trim().min(1).max(120);
/**
 * **Les rôles qu'un appel de provisionnement peut demander.**
 *
 * Deux, et pas quatre. `superadmin` est le compte qui voit tout, unique et créé
 * hors du produit ; `orgadmin` **ne peut pas être créé par une structure** — « une
 * structure ne peut pas s'en ajouter un elle-même » (Q11, Q12, F-02), et sa route
 * est distincte. Un schéma qui accepterait les quatre valeurs laisserait la règle
 * à la charge du service, donc à la charge de la vigilance.
 */
export const roleProvisionnableSchema = z
    .enum(['tutor', 'student'])
    .describe("Rôle du compte. Seuls « tutor » et « student » se provisionnent par appel : " +
    'un orgadmin est créé par le compte qui voit tout, sur sa route propre.');
/**
 * **Les cinq champs d'une personne, liste fermée** (Q5, F-03).
 *
 * « Cinq champs, liste fermée : nom, prénom, adresse email, rôle, identifiant de
 * la personne chez la structure. **Rien d'autre.** » **Aucune date de naissance :
 * le produit ne sait pas qui est mineur** — c'est `strictObject` qui tient cette
 * phrase, et le code d'erreur `CHAMP_NON_ACCEPTE` qui la rend lisible.
 */
export const personneProvisionneeSchema = z.strictObject({
    nom: nom.describe('Nom de famille de la personne.'),
    prenom: nom.describe('Prénom de la personne.'),
    email: email.describe("Adresse email de la personne. Clé de reconnaissance au provisionnement, et " +
        'seulement à ce moment-là : une adresse déjà connue retrouve son compte.'),
    role: roleProvisionnableSchema,
    identifiantExterne: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .describe("Identifiant de la personne chez la structure appelante."),
});
/**
 * `POST /api/v1/provisionnement/comptes` — F-03, et **chemin 2 de `CDC-2` F-13**.
 *
 * **Un seul appel peut créer à la fois le compte manquant, la relation et le
 * cours** (Q73), et **il est atomique** : si l'une des créations échoue, rien
 * n'est créé (Q87).
 *
 * **`cours` est SERVI depuis le LOT-2.** Il était déclaré et refusé par le LOT-1,
 * qui renvoyait à `CDC-2` : le déclarer d'avance a évité que le contrat change de
 * forme au lot suivant — un champ ajouté plus tard casse les fronts à la
 * compilation, ce que `DOCTRINE.md` §4 promet, mais casse aussi les appelants
 * extérieurs, qui ne compilent pas avec nous.
 */
export const provisionnementCompteSchema = personneProvisionneeSchema.extend({
    relationAvec: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .nullish()
        .describe("Identifiant Swype de la personne avec qui mettre celle-ci en relation. " +
        'Absent, aucune relation n’est créée.'),
    cours: z
        .strictObject({
        titre: nom.describe('Titre du cours.'),
        description: z
            .string()
            .trim()
            .min(1)
            .max(2000)
            .nullish()
            .describe('Description du cours. Obligatoire comme le titre : les deux le sont à ' +
            'la création, quel que soit le chemin emprunté.'),
    })
        .nullish()
        .describe('Cours à créer dans le même appel. Son couple est celui de la relation que ' +
        'cet appel pose : « relationAvec » désigne l’autre personne, et il est ' +
        'donc obligatoire quand ce champ est fourni. Les gardes de la création ' +
        'restent les mêmes que sur les deux autres chemins : relation existante, ' +
        'plafond de deux cours non terminés par tuteur, plafond d’élèves.'),
});
/**
 * `POST /api/v1/provisionnement/import` — F-04.
 *
 * **Cinq cents lignes au minimum doivent tenir** (Q118), **une seule ligne
 * invalide refuse le fichier entier** (Q119), et **aucun email ne part, quel que
 * soit le nombre de lignes** (Q156, Q172).
 *
 * Le fichier arrive **en texte dans le corps** et non en `multipart` : c'est un
 * CSV, séparateur point-virgule, encodage universel avec sa marque d'ordre
 * (Q531). Un envoi `multipart` aurait fait entrer ce lot dans le périmètre de
 * `secure-file-uploads` — signature binaire, stockage d'objets, URLs présignées —
 * pour un contenu qui n'est jamais conservé : le fichier est lu, validé, et
 * **son contenu ne laisse aucune trace** (Q531, règle 65 du domaine 10).
 */
export const importComptesSchema = z.strictObject({
    fichier: z
        .string()
        .min(1)
        // Cinq cents lignes de cinq champs tiennent très largement ; la borne est là
        // pour qu'un corps démesuré soit refusé avant d'être analysé.
        .max(2_000_000)
        .describe('Contenu du fichier CSV, séparateur point-virgule, en-tête : ' +
        'nom;prenom;email;role;identifiantExterne. Au moins cinq cents lignes ' +
        'doivent tenir. Aucun email ne part, quel que soit le nombre de lignes.'),
});
/**
 * `POST /api/v1/provisionnement/relations` — F-08.
 *
 * Les identifiants sont ceux **rendus par le produit** au provisionnement (Q61),
 * jamais des adresses : la reconnaissance par adresse ne vaut qu'au moment du
 * provisionnement (Q3, Q51).
 */
export const creationRelationSchema = z.strictObject({
    tuteurId: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .describe('Identifiant Swype du tuteur.'),
    eleveId: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .describe("Identifiant Swype de l'élève."),
});
/**
 * `POST /api/v1/structures/:id/orgadmins` — F-02.
 *
 * Pas d'`identifiantExterne` : « l'identifiant externe de son rattachement est
 * vide, et le champ est donc facultatif au modèle » (Q533). Pas de `role` non
 * plus : la route ne crée qu'un `orgadmin`.
 */
export const creationOrgadminSchema = z.strictObject({
    nom: nom.describe('Nom de famille de la personne.'),
    prenom: nom.describe('Prénom de la personne.'),
    email: email.describe("Adresse email. Une même personne ne tient pas l'espace de deux structures : " +
        'il lui faut deux comptes et deux adresses distinctes.'),
});
//# sourceMappingURL=provisionnement.contract.js.map